import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "react-bootstrap";
import { Link, useParams } from "react-router-dom";
import { DashboardLayout } from "@layouts/index";
import { getCautionFeeDetails } from "@services/krent.service";
import {
  formatCautionFeeStatus,
  getCautionFeeStatusStyle,
} from "../../helpers/statusStyles";
import { Image as ImageIcon } from "lucide-react";
import "./CautionFeeDetails.css";
import RefundRequestModal from "./RefundRequestModal";

const money = (value: unknown) =>
  typeof value === "number" && Number.isFinite(value)
    ? `₦${value.toLocaleString()}`
    : "—";

const getSteps = (displayStatus: string) => {
  const stepOrder = [
    "held",
    "eligible",
    "request_received",
    "under_review",
    "approved",
    "refund_paid",
    "disputed",
  ];

  // When paid, exclude the disputed step entirely
  const visibleSteps =
    displayStatus === "refund_paid"
      ? stepOrder.filter((key) => key !== "disputed")
      : stepOrder;

  const currentIndex = visibleSteps.indexOf(displayStatus);

  return visibleSteps.map((key) => {
    const stepConfig: any = {
      held: { label: "Held", description: "Stay is currently ongoing." },
      eligible: {
        label: "Eligible",
        description: "You can request your refund.",
      },
      request_received: {
        label: "Request received",
        description: "Your refund request is in the queue.",
      },
      under_review: {
        label: "Under review",
        description: "Verifying property condition (24–48h).",
      },
      approved: {
        label: "Approved",
        description: "Refund cleared and processing.",
      },
      refund_paid: {
        label: "Paid",
        description: "Funds in your bank account.",
      },
      disputed: {
        label: "Disputed",
        description: "Damage report filed.",
      },
    };

    const config = stepConfig[key];
    const stepIndex = visibleSteps.indexOf(key);

    let status = "pending";

    if (key === "disputed" && displayStatus === "disputed") {
      // Disputed is the current terminal state — mark it disputed (red dot)
      status = "disputed";
    } else if (key === displayStatus) {
      // Current active step
      status = "active";
    } else if (currentIndex !== -1 && stepIndex < currentIndex) {
      // Every step before the current one is completed — always show green
      status = "active";
    }

    return {
      key,
      label: config.label,
      description: config.description,
      status,
    };
  });
};

const CautionFeeDetails: React.FC = () => {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [showRefund, setShowRefund] = useState(false);
  const [requested, setRequested] = useState(false);

  // Get dynamic steps based on displayStatus
  const normalizedStatus = String(data?.displayStatus || "")
    .toLowerCase()
    .replace(/[ -]+/g, "_");
  const status = normalizedStatus === "paid" ? "refund_paid" : normalizedStatus;
  const steps = data ? getSteps(status) : [];
  const propertyImage = data?.propertyImageUrl || data?.propertyImage;

  useEffect(() => {
    if (id) {
      const abortController = new AbortController();
      setShowRefund(false);
      setRequested(false);
      setLoading(true);
      setError("");
      setData(null);
      getCautionFeeDetails(id, abortController.signal)
        .then((res: any) => {
          if (abortController.signal.aborted) return;
          if (!res?.data || res.success === false)
            throw new Error(
              res?.message || "Unable to load caution fee details.",
            );
          setData(res.data);
        })
        .catch((err) => {
          if (!abortController.signal.aborted)
            setError(err.message || "Unable to load caution fee details.");
        })
        .finally(() => {
          if (!abortController.signal.aborted) setLoading(false);
        });

      return () => {
        abortController.abort();
      };
    }
    setLoading(false);
    setError("Transaction not found.");
  }, [id, retry]);

  if (loading) {
    return (
      <DashboardLayout
        title="Caution fee Status details"
        subtitle="Here’s an overview of your caution fees and refund status."
      >
        <div className="caution-wrapper">
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "60vh",
              gap: "16px",
            }}
          >
            <div
              style={{
                width: "40px",
                height: "40px",
                border: "3px solid #e4e6ea",
                borderTop: "3px solid #2e9e8a",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            <p style={{ color: "#888", fontSize: "14px" }}>
              Loading caution fee details...
            </p>
          </div>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </DashboardLayout>
    );
  }

  if (error)
    return (
      <DashboardLayout
        title="Caution fee Status details"
        subtitle="Here’s an overview of your caution fees and refund status."
      >
        <div role="alert" className="cfd-wrapper">
          <p>{error}</p>
          <Button onClick={() => setRetry((value) => value + 1)}>
            Try again
          </Button>{" "}
          <Link to="/caution-fee">Back to my Caution fee</Link>
        </div>
      </DashboardLayout>
    );

  return (
    <DashboardLayout
      title="Caution fee Status details"
      subtitle="Here’s an overview of your caution fees and refund status."
    >
      <div className="cfd-wrapper">
        <Link className="cfd-back-link" to="/caution-fee">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M10 12L6 8L10 4"
              stroke="#1a1a1a"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Back to my Caution fee
        </Link>

        {/* Property Card */}
        <div className="cfd-property-card">
          <div className="cfd-property-card-left">
            <div className="cfd-property-img">
              {typeof propertyImage === "string" && propertyImage ? (
                <img
                  src={propertyImage}
                  alt={data?.propertyName || "Property"}
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <ImageIcon size={28} aria-label="Property image unavailable" />
              )}
            </div>
            <div>
              <div className="cfd-property-name">{data?.propertyName}</div>
              <div className="cfd-property-ref">
                Ref: {data?.bookingReference}
              </div>
              <div
                style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
              >
                {data?.stayDates?.label}
              </div>
            </div>
          </div>
          <span
            className="cfd-held-badge px-4 rounded-4"
            style={getCautionFeeStatusStyle(data?.displayStatus)}
          >
            {formatCautionFeeStatus(data?.displayStatus)}
          </span>
        </div>

        {/* Alert Banner */}
        <div className="cfd-caution-alert">
          <div>
            <div className="cfd-alert-title">
              {`${status === "eligible" ? "Refund eligible" : `Caution fee ${formatCautionFeeStatus(data?.displayStatus).toLowerCase()}`} — ${data?.propertyName || "Property"}`}
            </div>
            <p className="cfd-alert-subtitle">
              {data?.statusMessage ||
                steps.find((step) => step.key === status)?.description}
            </p>
          </div>
          {status === "eligible" && !requested && (
            <button
              type="button"
              className="cfd-request-button"
              onClick={() => setShowRefund(true)}
            >
              Request refund
            </button>
          )}
        </div>

        {/* Main Content */}
        <Row className="g-3">
          {/* Payment Summary */}
          <Col lg={7}>
            <div className="cfd-summary-card">
              <h5>Payment summary</h5>
              <div className="cfd-summary-row">
                <span className="cfd-summary-label dark">Property name</span>
                <span className="cfd-summary-value">{data?.propertyName}</span>
              </div>
              <div className="cfd-summary-row">
                <span className="cfd-summary-label dark">
                  Caution fee deposit
                </span>
                <span className="cfd-summary-value">
                  {money(data?.cautionFee)}
                </span>
              </div>
              <div className="cfd-summary-row">
                <span className="cfd-summary-label">Damage claimed</span>
                <span className="cfd-summary-value no">
                  {money(data?.damageClaimed)}
                </span>
              </div>

              <div className="cfd-summary-row">
                <span className="cfd-summary-label">Potential refund</span>
                <span className="cfd-summary-value teal">
                  {money(data?.potentialRefund)}
                </span>
              </div>
              <div className="cfd-summary-row">
                <span className="cfd-summary-label">Expected refund</span>
                <span className="cfd-summary-value teal">
                  {money(data?.expectedRefund)}
                </span>
              </div>
              {data?.hasBankAccount === false && (
                <div className="cfd-summary-row">
                  <span
                    className="cfd-summary-label"
                    style={{ color: "#f59e0b" }}
                  >
                    Bank Account
                  </span>
                  <span
                    className="cfd-summary-value"
                    style={{ color: "#f59e0b" }}
                  >
                    Not added
                  </span>
                </div>
              )}
              {data?.bankAccountMessage && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "12px",
                    backgroundColor: "#fef9c3",
                    border: "1px solid #fde047",
                    borderRadius: "8px",
                    fontSize: "13px",
                    color: "#854d0e",
                  }}
                >
                  {data.bankAccountMessage}
                </div>
              )}
            </div>
            <a href="mailto:support@krent.ng" className="btn cfd-contact-btn">
              Contact support
            </a>
          </Col>

          <Col lg={5}>
            <div className="cfd-timeline-card">
              <h5>What happens next?</h5>
              <div className="cfd-timeline">
                {steps.map((step, index) => (
                  <div className="cfd-timeline-item" key={step.key}>
                    <div className="cfd-timeline-dot-col">
                      <div className={`cfd-timeline-dot ${step.status}`} />
                      {index < steps.length - 1 && (
                        <div className="cfd-timeline-line" />
                      )}
                    </div>
                    <div className="cfd-timeline-content">
                      <div className={`cfd-timeline-label ${step.status}`}>
                        {step.label}
                      </div>
                      <div className={`cfd-timeline-desc ${step.status}`}>
                        {step.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>
        {showRefund && id && (
          <RefundRequestModal
            transactionId={id}
            onClose={() => {
              setShowRefund(false);
              if (requested) setRetry((value) => value + 1);
            }}
            onSuccess={() => {
              setRequested(true);
              setData((current: any) => ({
                ...current,
                displayStatus: "request_received",
                statusMessage: "Your refund request is in the queue.",
              }));
            }}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default CautionFeeDetails;
